import { DynamicGroupEvent } from 'types/DynamicGroupEvent';
import { DynamicPropertyEvent } from 'types/DynamicPropertyEvent';
import { DynamicPropertyStatus } from 'types/DynamicPropertyStatus';
import { DynamicPropertyType } from 'types/DynamicPropertyType';
import {
  arrayMapFilter,
  arrayReject,
  arrayRemoveFirstMatching,
} from 'utils/Array';
import DisposerManager from 'utils/DisposerManager';
import {
  promptForJsonFile,
  type PromptForJsonFileResult,
} from 'utils/FileUtils';

import { type CSVAdRowData } from '../../AdsManager';
import Store, { type BecomeObservableOptions } from '../../Store';
import { type DynamicPropertyData, isDynamicPropertyData } from '../data';
import { type IDynamicProperty } from '../IDynamicProperty';
import { parseDimensions, parseUnitValue } from '../parse';
import { mapNamedProperties } from '../transform';
import { getOwningCreative } from '../tree';
import type Asset from './Asset';
import { AllTypes } from './registry';

export type FigmaImageLayer = {
  type: 'figmaImageNode';
  name: string;
  x: string;
  y: string; 
  width: string;
  height: string;
  data: string; // base64 image data
  asset: string; // path to asset
  scale: number;
  compression: string;
  quality: number;
  checksum: string;
}

export const DEFAULT_STATUS_MESSAGE = 'Loading...';

function propertyPut(
  liveProperty: IDynamicProperty<any>,
  storedProperty: DynamicPropertyData
) {
  if (liveProperty.type === storedProperty.type) {
    if (liveProperty.setState) {
      liveProperty.setState(storedProperty.value);
    } else {
      liveProperty.set(storedProperty.value, true);
    }
    return true;
  }

  const logName = JSON.stringify(liveProperty.name);
  const logLiveType = JSON.stringify(liveProperty.type);
  const logStoredType = JSON.stringify(storedProperty.type);
  console.warn(
    `[propertyPut] Property type mismatch for ${logName}: Existing type is ${logLiveType}, incoming type is ${logStoredType}. Incoming value ignored.`
  );
  return false;
}

function preenStoredProperties(data: unknown): DynamicPropertyData[] {
  const logPrefix = '[preenStoredProperties]';

  if (!Array.isArray(data)) {
    console.warn(`${logPrefix} data is not an Array`);
    return [];
  }

  return data.filter((pd, i) => {
    if (isDynamicPropertyData(pd)) {
      return true;
    }
    console.warn(
      `${logPrefix} data[${i}] is not a valid DynamicPropertyData. Ignoring.`,
      pd
    );
    return false;
  });
}

interface PropertyMatch {
  storedProperty: DynamicPropertyData | undefined;
  liveProperty: IDynamicProperty<any> | undefined;
}

function matchStoredPropertiesToLive(
  storedProperties: ReadonlyArray<DynamicPropertyData>,
  liveProperties: ReadonlyArray<IDynamicProperty<any>>
) {
  const unclaimedProperties = [...liveProperties];

  // For each stored property, first try finding a live property with the
  // same name *and* type.
  const matches = storedProperties.map((sp): PropertyMatch => {
    return {
      storedProperty: sp,
      liveProperty: arrayRemoveFirstMatching(
        unclaimedProperties,
        (lp) => lp.name === sp.name && lp.type === sp.type
      ),
    };
  });

  // Take a second pass over the list, this time 'matching' by name only.
  // Currently, the caller will see the type mismatch and just emit a
  // diagnostic without writing the value
  for (const match of matches) {
    if (match.storedProperty && !match.liveProperty) {
      const sp = match.storedProperty;
      match.liveProperty = arrayRemoveFirstMatching(
        unclaimedProperties,
        (lp) => lp.name === sp.name
      );
    }
  }

  // By this point, all stored properties have had two chances to find the
  // best-matching live property. any ones with an undefined liveProperty
  // will have one created for them by the caller.

  // Append every remaining unclaimed property with no storedProperty, so
  // the result is a complete account of both inputs rather than only the
  // pairs that matched - akin to a FULL OUTER JOIN in SQL terms.
  //
  // What actually becomes of an unmatched live property is the caller's
  // policy, not this function's: today it drops them, which is what every
  // prior setState did - a property absent from the stored state is gone.
  for (const unclaimed of unclaimedProperties) {
    matches.push({
      storedProperty: undefined,
      liveProperty: unclaimed,
    });
  }

  return matches;
}

export default class DynamicGroup
  extends Store
  implements IDynamicProperty<IDynamicProperty<any>[]>
{
  protected readonly disposer = new DisposerManager();

  label = '';
  value: IDynamicProperty<any>[] = [];
  // Initialized explicitly so becomeObservable() picks it up - a field with
  // no initializer emits nothing under useDefineForClassFields:false, so it
  // is not an own property when Reflect.ownKeys runs and gets silently
  // skipped.
  //
  // Stays `| undefined` rather than defaulting to []: setState() treats
  // undefined as a "state has never been set" sentinel (its `if
  // (!this.initialValue)` guard), and an empty array is truthy, so [] would
  // disable that branch without any error.
  initialValue: IDynamicProperty<any>[] | undefined = undefined;
  properties: IDynamicProperty<any>[] = [];
  name = '';
  type = DynamicPropertyType.GROUP;
  status = DynamicPropertyStatus.READY;
  statusMessage = DEFAULT_STATUS_MESSAGE;
  parent: DynamicGroup | null | undefined;
  isStateEnabled = false;
  count = 0;

  // Set when properties are added or removed; cleared by setState and
  // resetChanged. getChanged() combines it with the children's own state.
  private structureChanged = false;

  getChanged = () => {
    return this.structureChanged || this.properties.some((p) => p.getChanged());
  };

  get loading() {
    // return if any property is loading
    return this.properties.some(
      (p) => p.status === DynamicPropertyStatus.LOADING
    );
  }

  constructor(
    parent?: DynamicGroup | null,
    name?: string,
    value?: any
  ) {
    super();
    this.parent = parent;
    if (name) this.name = name;
    this.setState(value);
    this.becomeObservable();
  }

  /**
   * Keeps `disposer` out of MobX. See the note on `neverObserve` in Store.ts
   * for why a base class has to do this by override.
   */
  becomeObservable(options?: BecomeObservableOptions) {
    super.becomeObservable({
      except: [...(options?.except ?? []), 'disposer'],
    });
  }

  enableState = () => {
    this.isStateEnabled = true;
    this.becomeObservable();
    for (const property of this.properties) {
      if (property instanceof DynamicGroup) {
        property.enableState();
      } else {
        property.becomeObservable();
      }
    }
  }

  transform = (value: any) => {
    return value;
  }

  checkExists = (property: IDynamicProperty<any>) => {
    return !!this.getExact(property.name, property.type, property.value);
  }

  revert = () => {
    for (const property of this.properties) {
      property.revert();
    }
    this.dispatch(DynamicPropertyEvent.CHANGED);
  };

  /**
   * Called when a child that setState created dispatches CHANGED. By default
   * the change is passed up as this group's own. A subclass can override this
   * to react first: SVGText re-renders, and dispatches CHANGED itself once the
   * new SVGData is in place.
   */
  protected onChildChanged(): void {
    this.dispatch(DynamicPropertyEvent.CHANGED);
  }

  getChild = (name: string) => {
    return this.properties.find(item => item.name === name);
  }

  getAllPropertiesNames = () => {
    const names = this.properties.map((property) => property.name);
    return [... new Set(names)]
  }

  /**
   * Not supported: a group's value is its children, and they are set
   * individually. This exists only because IDynamicProperty requires a `set`,
   * and so takes the interface's parameters.
   *
   * It used to be `set(name, value, silent)`, setting the named child. A call
   * meant for a leaf's `set(value, silent)` that landed on a group would then
   * look up a child *named* by the value - usually finding none, occasionally
   * setting the wrong one - and dispatch CHANGED either way. Leaf-style calls
   * can still reach a group, e.g. merge() where a leaf and a group share a
   * name, or Figma data giving a plain value for a group's key, so this warns
   * and does nothing rather than throwing.
   */
  public set(value: IDynamicProperty<any>[], silent?: boolean) {
    console.warn(
      `[DynamicGroup.set] Ignored for group ${JSON.stringify(this.name)}: set the group's children individually`,
      { value, silent }
    );
  }

  render = () => {
    // for use in child classes that need to render values.
  }

  reset = () => {
    this.properties = [];
    this.value = [];
    this.initialValue = [];
    this.status = DynamicPropertyStatus.READY;
  };

  resetChanged = () => {
    for (const property of this.properties) {
      property.resetChanged();
    }
    this.initialValue = this.value;
    this.structureChanged = false;
    this.dispatch(DynamicPropertyEvent.CHANGED);
  };

  public add(name: string, typeName: string, value: any) {
    const properties = [...this.properties];
    const newProperty = this.create(typeName, name, value);

    if (!newProperty) {
      console.warn(
        `[DynamicGroup.add] Error creating non-existent type ${JSON.stringify(typeName)}`
      );
      return;
    }
    properties.push(newProperty);

    this.properties = properties;
    this.value = properties;
    this.structureChanged = true;

    this.dispatch(DynamicGroupEvent.ADDED);
    this.dispatch(DynamicPropertyEvent.CHANGED);
    return newProperty;
  }

  create = (
    type: string,
    name: string,
    value: any
  ): IDynamicProperty<any> | undefined => {
    const typeClass = Object(AllTypes)[type];
    if (!typeClass) {
      return undefined;
    } else {
      const newProp = new typeClass(this, name, value) as IDynamicProperty<any>;
      const valueIsNullish = value == null;

      if (valueIsNullish) {
        if (newProp.setDefault) newProp.setDefault();
      } else {
        // Re-assigning here is not belt-and-braces. DynamicProperty's
        // constructor does assign `value`, but every subclass declares
        // `value` as a class field, and derived field initialisers run
        // immediately after super() returns - so they clobber it with the
        // subclass default. Every property therefore leaves its constructor
        // holding '' / 0 / [] rather than what was passed, and this is where
        // it gets put right.
        value = newProp.transform(value);
        newProp.value = value;
        newProp.initialValue = value;
      }

      // Renders last, so it sees the value settled by whichever branch ran
      // above. It used to sit inside the else, *before* the re-assignment,
      // which meant Label.render() always ran against the clobbered '' and
      // produced an empty `rendered` - and on the defaulted path it never ran
      // at all.
      if (newProp.render) newProp.render();

      if (this.isStateEnabled) newProp.becomeObservable();
      return newProp;
    }
  };

  addProperty = (property: IDynamicProperty<any>) => {
    return this.add(property.name, property.type, property.value);
  }

  addGroup = (name: string, properties: IDynamicProperty<any>[]) => {
    console.log('add group', name, properties);
    const copiedProperties = properties.map((property) => {
      return {
        name: property.name,
        type: property.type,
        value: property.value,
      };
    });

    const group = new DynamicGroup(this, name, copiedProperties);
    // properties and value are routinely assigned the same array (setState,
    // add, remove...). Today MobX copies each assignment into its own
    // observable array, so they never actually alias - but don't make that
    // load-bearing, or this would push the group twice.
    this.properties.push(group);
    if (this.value !== this.properties) this.value.push(group);
    this.dispatch(DynamicGroupEvent.ADDED);
    this.dispatch(DynamicPropertyEvent.CHANGED);
    return group;
  };

  remove(name: string) {
    let properties = [...this.properties];
    const property = properties.find((item) => item.name === name);
    if (property) {
      properties = properties.filter((item) => item.name !== name);
      this.properties = properties;
      this.value = properties;
      this.dispatch(DynamicPropertyEvent.CHANGED);
    }
  }

  removeProperty(property: IDynamicProperty<any>) {
    const properties = arrayReject(
      this.properties,
      (p) =>
        p.name === property.name &&
        p.type === property.type &&
        p.value === property.value
    );
    this.properties = properties;
    this.value = properties;
    this.structureChanged = true;
    this.dispatch(DynamicPropertyEvent.CHANGED);
  }

  setState = (data: any) => {
    const logPrefix = '[DynamicGroup.setState]';

    // Nullish and an empty Array are deliberately *not* equivalent here.
    // This parameter does double duty: it carries the state, and also
    // whether any state was supplied at all.
    //
    //   null / undefined   nothing to say - leave this.properties alone
    //   []                 the stored state is empty - remove them all
    //
    // The distinction is load-bearing because callers hand a Firestore
    // field straight through, and an absent field arrives as undefined -
    // Ad passes `data.dynamicData` and Creative passes `data.filterSets`,
    // neither guarded. Interpreting those as an empty Array would let a
    // document that has simply never written the field wipe every live
    // property on it.
    //
    // The same nullish-vs-empty asymmetry appears again below, where
    // `!this.initialValue` is the "state has never been set" sentinel; see
    // that field's comment for why [] cannot serve there either.
    if (data == null) return;

    const storedProperties = preenStoredProperties(data);
    const propertyMatches = matchStoredPropertiesToLive(
      storedProperties,
      this.properties
    );
    const newProperties = arrayMapFilter(
      propertyMatches,
      ({ storedProperty, liveProperty }, i) => {
        if (storedProperty) {
          if (liveProperty) {
            // stored property with best matching live property; set value
            propertyPut(liveProperty, storedProperty);
            return liveProperty;
          }

          // otherwise stored property with no live property; create one
          const newLiveProperty = this.create(
            storedProperty.type,
            storedProperty.name,
            storedProperty.value
          );
          if (!newLiveProperty) {
            const logName = JSON.stringify(storedProperty.name);
            const logType = JSON.stringify(storedProperty.type);
            console.warn(
              `${logPrefix} Unknown type ${logType} for property ${logName}. Ignoring.`
            );
            return undefined;
          }
          // Hear the new property's changes - see onChildChanged. Only
          // properties created here get this: ones added via add(),
          // addProperty() or addGroup() never pass theirs up.
          newLiveProperty.addEventListener(DynamicPropertyEvent.CHANGED, () =>
            this.onChildChanged()
          );
          if (newLiveProperty instanceof DynamicGroup) {
            // A group's transform is the identity, so none is applied here.
            newLiveProperty.setChildInitialValues(storedProperty.value);
          } else {
            newLiveProperty.setInitialValue(
              newLiveProperty.transform(storedProperty.value)
            );
          }
          // ^ setInitialValue's work is mostly superseded by propertyPut.
          // Also, the first thing done by setInitialValue (in the Property
          // impl at least) is call this.transform, so this is getting
          // `transform`ed twice?
          propertyPut(newLiveProperty, storedProperty);

          if (newLiveProperty.value === undefined) {
            const logName = JSON.stringify(storedProperty.name);
            const logType = JSON.stringify(storedProperty.type);
            console.warn(
              `${logPrefix} Failed to init new property ${logName}, type ${logType}`
            );
            // Matches original code - if a created property failed to set
            // a value, it was not committed to the Group
            return undefined;
          }
          return newLiveProperty;
        }

        if (liveProperty) {
          // live property with no stored property; this gets dropped
          return undefined;
        }

        // neither stored nor live - shouldn't happen, but technically
        // allowed by the type. consider this a non-fatal assertion failure
        console.warn(
          `${logPrefix} propertyMatches[${i}] has neither storedProperty nor liveProperty`
        );
        return undefined;
      }
    );

    this.properties = newProperties;
    this.value = newProperties;

    // Only update initialValue if it's the first time setting the state
    if (!this.initialValue) {
      this.initialValue = newProperties;
    }

    // Keep the changed status as it was before
    this.structureChanged = false;

    // Dispatch the change event
    this.dispatch(DynamicPropertyEvent.CHANGED);
  };

  getState = (): DynamicPropertyData => {
    const values = this.properties.map((item) => item.getState());
    return {
      value: values,
      name: this.name,
      type: this.type,
    };
  };

  getString = () => {
    return JSON.stringify(
      mapNamedProperties(this.properties, (p) => p.getString())
    );
  }

  getPreviewData = (): any => {
    return mapNamedProperties(this.properties, (p) => p.getPreviewData());
  }

  getCSVData = () => {
    return JSON.stringify(
      mapNamedProperties(this.properties, (p) => p.getCSVData())
    );
  }

  getExact = (name: string, type: string, value: any) => {
    return this.properties.find(
      (item) => item.name === name && item.type === type && item.value === value
    );
  }

  /**
   * Not supported, like set(): a group's value is its children, and they
   * are initialized individually - see setChildInitialValues. This exists
   * only because IDynamicProperty requires a `setInitialValue`.
   */
  public setInitialValue(value: IDynamicProperty<any>[]) {
    console.warn(
      `[DynamicGroup.setInitialValue] Ignored for group ${JSON.stringify(this.name)}: initialize the group's children individually`,
      { value }
    );
  }

  /**
   * Gives each child the initial value found for it, by name, in `data` - the
   * serialized form a group is stored in, an array of { name, type, value },
   * not an object keyed by name. Recurses into child groups.
   *
   * Children with no matching entry are skipped rather than initialized to
   * undefined: a child's setInitialValue assigns `value` too, so calling it for
   * something the incoming data does not mention would erase a live value.
   *
   * Like the group-level setInitialValue it replaces, this leaves the group's
   * own initialValue alone.
   */
  public setChildInitialValues(data: unknown) {
    if (!Array.isArray(data)) return;
    for (const property of this.properties) {
      const propertyData = data.find((item) => item?.name === property.name);
      if (!propertyData) continue;
      if (property instanceof DynamicGroup) {
        property.setChildInitialValues(propertyData.value);
      } else {
        property.setInitialValue(propertyData.value);
      }
    }
  }

  setUsingCSV = (value: string) => { // TODO: Supporting nested groups is currently unsupported.
    const data = JSON.parse(value);
    // console.log('Dynamic Properties Manager: Setting using CSV: ', data);
    for (const name in data) {
      const value = data[name];
      const property = this.getChild(name);
      // console.log('++setUsingCSV: Setting property: ', name, ' to: ', value);
      if (property && property.setUsingCSV) {
        property.setUsingCSV(value);
      }
    }
  }

  setUsingCSVRowObject = (properties: CSVAdRowData) => {
    const ignore = ['id', 'adID'];
    for (const name in properties) {
      if (ignore.includes(name)) continue;
      const value = Object(properties)[name];
      const property = this.getChild(name);
      if (property && property.setUsingCSV) {
        // console.log('++setUsingCSVRowObject: Setting property: ', name, ' to: ', value);
        property.setUsingCSV(value);
      }
    }
  }

  setUsingFigmaData = async (data: any) => {
    // First process any images that need to be uploaded/updated
    this.status = DynamicPropertyStatus.LOADING;
    this.statusMessage = 'Teleporting...';
    await this.processFigmaImageLayers(data);
    
    // Continue with existing figma data processing
    // Handle root level properties first
    for (const [key, value] of Object.entries(data)) {
      const property = this.getChild(key);
      
      if (!property) {
        console.warn(`No matching property found for "${key}"`);
        continue;
      }

      // Special handling for 'name' -> 'label' mapping
      if (key === 'name' && this.getChild('label')) {
        this.getChild('label')?.set(value);
        continue;
      }

      // Handle dimensions special case
      if (key === 'dimensions' && typeof value === 'string') {
        // Was `.map(v => parseInt(v))`, which truncated fractional pixels and
        // did not check the split produced two halves - "300" set height to
        // NaN. Now the shared parser, which is the same length-and-finite check
        // this had grown, plus a case-insensitive separator - so Figma data
        // reads `300X250` the same way CSV import and the tearsheet do.
        const axes = parseDimensions(value);
        if (!axes) {
          console.warn(
            `setUsingFigmaData: unreadable dimensions for "${key}"`,
            value
          );
          continue;
        }

        const [width, height] = axes;
        const dimensionsGroup = property as DynamicGroup;
        dimensionsGroup.getChild('width')?.set(width);
        dimensionsGroup.getChild('height')?.set(height);
        continue;
      }

      // Handle pixel values at root level
      // parseUnitValue rather than endsWith + parseFloat: it validates the
      // unit instead of pattern-matching the suffix, and does not silently
      // accept a numeric prefix followed by anything.
      if (typeof value === 'string') {
        const parsed = parseUnitValue(value);
        if (parsed && parsed.type === 'px') {
          property.set(parsed.value);
          continue;
        }
      }

      // Handle layer objects
      if (typeof value === 'object' && value !== null) {
        if (property instanceof DynamicGroup) {
          this.handleFigmaLayerProperties(property, value);
        }
        continue;
      }

      // Handle simple value types
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        property.set(value);
      }
    }
    this.status = DynamicPropertyStatus.READY;
    this.statusMessage = DEFAULT_STATUS_MESSAGE;
  }

  private handleFigmaLayerProperties(propertyGroup: DynamicGroup, layerData: any): void {
    // Map each property in the layer object to its corresponding dynamic property
    for (const [key, value] of Object.entries(layerData)) {
      const property = propertyGroup.getChild(key);
      if (!property) continue;

      // Handle pixel values by stripping 'px' and converting to number
      // parseUnitValue rather than endsWith + parseFloat: it validates the
      // unit instead of pattern-matching the suffix, and does not silently
      // accept a numeric prefix followed by anything.
      if (typeof value === 'string') {
        const parsed = parseUnitValue(value);
        if (parsed && parsed.type === 'px') {
          property.set(parsed.value);
          continue;
        }
      }

      if(key === 'asset' && typeof value === 'string') {
        const asset = property as Asset;
        const assetsManager = getOwningCreative(this)?.assets;
        if(!assetsManager) return;
        const assetPath = value;
        const assetFile = assetsManager.getByPath(assetPath);
        if(!assetFile) {
          console.warn(`setUsingFigmaData: Asset not found: ${assetPath}`);
          continue;
        }
        asset.set(assetFile.id);
        continue;
      }
      
      // Handle all other values directly
      property.set(value);
    }
  }

  private processFigmaImageLayers = async (data: any) => {
    // Only process if we have access to the creative and assets manager.
    // Captured once rather than re-resolved at each of the three uses below:
    // this method awaits uploads, and re-reading the global mid-flight was how
    // a navigation could land the images in a different creative's assets.
    const assetsManager = getOwningCreative(this)?.assets;
    if (!assetsManager) return;

    const imageLayerKeys = Object.keys(data).filter(key => 
      key.endsWith('Layer') && 
      data[key].type === 'figmaImageNode'
    );

    console.log(`Starting parallel upload of ${imageLayerKeys.length} Figma image layers...`);

    // Create array of upload promises
    const imageUploads = imageLayerKeys.map(async (layerKey) => {
      const layer: FigmaImageLayer = data[layerKey];
      
      // Skip if no asset path or base64 data
      if (!layer.asset || !layer.data) {
        console.log(`Skipping ${layerKey}: Missing asset path or data`);
        return;
      }

      const uploadStartTime = performance.now();
      try {
        // Convert base64 to blob
        const base64Data = layer.data.split(',')[1];
        const blob = await fetch(`data:image/png;base64,${base64Data}`).then(r => r.blob());

        // Create file from blob with the filename from the asset path

        const parentFolderPathParts = layer.asset.split('/');
        const leafName = parentFolderPathParts.pop() || 'image.png';
        parentFolderPathParts.unshift('teleport');
        const parentFolderPath = parentFolderPathParts.join('/');
        const assetFilePath = (layer.asset = [
          ...parentFolderPathParts,
          leafName,
        ].join('/'));

        const file = new File([blob], leafName, {
          type: 'image/png'
        });

        // Check if asset exists
        const existingAsset = assetsManager.getByPath(assetFilePath);
        
        console.log(`Starting upload for ${layerKey}:`, {
          leafName,
          parentFolderPath,
          assetFilePath,
          fileSize: `${(blob.size / 1024).toFixed(2)}KB`,
          exists: !!existingAsset,
          checksum: layer.checksum
        });

        if (existingAsset?.data.checksum === layer.checksum) {
          console.log(
            `⏭️ Skipping ${leafName}: Asset exists with matching checksum`,
          );
        } else {
          await assetsManager.uploadToAssetFolder(
            file,
            parentFolderPath,
            {
              postProcess: false,
              overrideFileName: leafName,
              checksum: layer.checksum,
            }
          );
          const uploadDuration = (
            (performance.now() - uploadStartTime) /
            1000
          ).toFixed(2);
          const report = existingAsset ? 'Asset updated' : 'New asset uploaded';
          console.log(`✅ ${report}: ${leafName} (took ${uploadDuration}s)`);
        }
      } catch (error) {
        console.error(`❌ Failed to process Figma image layer ${layerKey}:`, error);
        throw error;
      }
    });

    // Wait for all uploads to complete
    await Promise.all(imageUploads);
  };

  async promptAndLoadFigmaDataFile(): Promise<boolean> {
    let result: PromptForJsonFileResult;
    try {
      result = await promptForJsonFile();
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error;
      // Keep the user-facing message short, but log the parser's own error
      // so there's something concrete to go on in a support conversation.
      console.error('promptAndLoadFigmaDataFile: invalid JSON:', error);
      throw new Error('Invalid JSON file');
    }

    if (!result.accepted) {
      // User canceled or no file selected
      return false;
    }

    const figmaData = result.value;
    this.validateFigmaData(figmaData);
    // Process the data
    await this.setUsingFigmaData(figmaData);
    return true;
  }

  validateFigmaData = (data: any) => {
    if (typeof data !== 'object' || data === null) {
      throw new Error('Invalid Figma data: must be an object');
    }

    if (!data.name || typeof data.name !== 'string') {
      throw new Error('Invalid Figma data: missing or invalid "name" property');
    }

    if (!data.borderThickness || typeof data.borderThickness !== 'string' || !data.borderThickness.endsWith('px')) {
      throw new Error('Invalid Figma data: missing or invalid "borderThickness" property (must end with px)');
    }

    // Check if we have access to the creative and current ad. The comment
    // always said "the creative and", but the check only ever covered `ads` -
    // the creative itself was assumed. Now both are actually covered.
    if (!getOwningCreative(this)?.ads.current) {
      throw new Error('No ad selected');
    }
  }

}
